import { useEffect } from "react";
import {
  createFileRoute,
  getRouteApi,
  notFound,
  redirect,
} from "@tanstack/react-router";
import { trackPageView } from "@/hooks/useAnalytics";
import { getJobs, getTagBySlug } from "@/lib/api";
import type { PaginatedJobs } from "@/types/db";
import NotFoundPage from "@/components/common/NotFoundPage";
import TagListingView from "@/components/common/tagListingView";
import { MIN_INDEXABLE_JOBS } from "@/lib/listingSeo";
import { getTagMetaDescription, getTagMetaTitle } from "@/lib/tagCopy";

import { splitTags } from "@/lib/tagFilterUrl";

const SITE_URL = "https://datafirstjobs.com";
const rootRoute = getRouteApi("__root__");
const PATH_IMPLICIT_TAGS = ["remote-data-jobs"];

interface RemoteSlugSearch {
  /** Comma-separated slugs. */
  tags?: string;
}

export const Route = createFileRoute("/remote/$slug")({
  validateSearch: (search: Record<string, unknown>): RemoteSlugSearch => ({
    tags: typeof search.tags === "string" ? search.tags : undefined,
  }),
  loaderDeps: ({ search: { tags } }) => ({ tags }),
  loader: async ({ params, deps }) => {
    if (params.slug === "remote-data-jobs") {
      throw redirect({ to: "/remote", statusCode: 301 });
    }
    const extraTags = splitTags(deps.tags).filter(
      (s) => s !== params.slug && !PATH_IMPLICIT_TAGS.includes(s),
    );
    const effectiveTags = [...PATH_IMPLICIT_TAGS, params.slug, ...extraTags];
    const [tag, initialData] = await Promise.all([
      getTagBySlug({ data: params.slug }),
      getJobs({
        data: { limit: 20, tags: effectiveTags },
      }) as Promise<PaginatedJobs>,
    ]);

    if (!tag) {
      throw notFound();
    }

    return {
      tag,
      initialJobs: initialData.jobs,
      initialTotal: initialData.total,
      extraTagSlugs: extraTags,
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};

    const title = getTagMetaTitle(loaderData.tag, { isRemote: true });
    const description = getTagMetaDescription(
      loaderData.tag,
      loaderData.initialTotal,
      { isRemote: true },
    );
    const url = `${SITE_URL}/remote/${loaderData.tag.slug}`;
    const isThin = loaderData.initialTotal < MIN_INDEXABLE_JOBS;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(isThin ? [{ name: "robots", content: "noindex, follow" }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: RemoteTagPage,
  notFoundComponent: NotFoundPage,
});

function RemoteTagPage() {
  const data = Route.useLoaderData();
  const { jobCategoriesWithTags, locations } = rootRoute.useRouteContext();

  useEffect(() => {
    trackPageView("search");
  }, []);

  return (
    <TagListingView
      variant="remote"
      tag={data.tag}
      initialJobs={data.initialJobs}
      initialTotal={data.initialTotal}
      tagStats={null}
      extraTagSlugs={data.extraTagSlugs}
      jobCategoriesWithTags={jobCategoriesWithTags}
      locations={locations}
    />
  );
}
